import { createContext, useContext, useLayoutEffect, useMemo, useRef, type ReactNode } from "react";
import { ErrorBanner, Loading, createApiClient, useResource, useSession } from "@gweb/ui";
import { makeApi, type SalesApi } from "./api.js";
import type { PipelineConfig } from "./types.js";

const ApiCtx = createContext<SalesApi | null>(null);
const PipelineCtx = createContext<PipelineConfig | null>(null);

export function useApi(): SalesApi {
  const v = useContext(ApiCtx);
  if (!v) throw new Error("useApi must be used inside ApiProvider");
  return v;
}
export function usePipeline(): PipelineConfig {
  const v = useContext(PipelineCtx);
  if (!v) throw new Error("usePipeline must be used inside PipelineProvider");
  return v;
}

export function ApiProvider({ apiBase, fetchImpl, children }: { apiBase: string; fetchImpl?: typeof fetch; children: ReactNode }) {
  const { token, signOut } = useSession();
  const tokenRef = useRef(token);
  useLayoutEffect(() => { tokenRef.current = token; });
  const api = useMemo(() => makeApi(createApiClient({ baseUrl: apiBase, getToken: () => tokenRef.current, onUnauthorized: signOut, ...(fetchImpl && { fetchImpl }) })), [apiBase, fetchImpl, signOut]);
  return <ApiCtx.Provider value={api}>{children}</ApiCtx.Provider>;
}

/** The pipeline rules come from the server once per visit, so every page agrees with it. */
export function PipelineProvider({ children }: { children: ReactNode }) {
  const api = useApi();
  const r = useResource((s) => api.pipeline(s), [api]);
  if (r.data) return <PipelineCtx.Provider value={r.data}>{children}</PipelineCtx.Provider>;
  if (r.error) return <ErrorBanner error={r.error} title="Could not load the pipeline rules" />;
  return <Loading label="Loading the pipeline" />;
}
